import React, { useState, useContext, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, InputLabel, MenuItem, Snackbar } from '@mui/material';
import LoadingSpinner from '../../components/LoadingSpinner';
import PropTypes from 'prop-types';
import {
  getUserRoles,
  getUserDetails,
  postUserDetails,
  putUserDetails,
  disableUser,
  resendUserInvite,
} from '../../api/settings';
import { StoreContext } from '../../components/Store';
import { dateFormatChange } from '../../utils/date';
import { isValidEmail } from '../../utils/validation';
import { isITUser } from '../../utils/roles';

const paginationModel = { page: 0, pageSize: 50 };

export default function DataTable({ companyId, isReadOnly, customerType }) {
  const [rowData, setRowData] = useState([]);
  const [addUser, setAddUser] = useState(false);
  const [apiError, setApiError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const store = useContext(StoreContext);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarBackgroundColor, setSnackbarBackgroundColor] = useState('red');

  const currentCompanyId = companyId || store.eoriCompany[0] || sessionStorage.getItem('companyid');

  const disableEORIEditing = [1, '1'].includes(currentCompanyId) && !isITUser(); // only IT user can add/edit EORI users
  const disableEditing = isReadOnly || disableEORIEditing;

  // Only show MFA field for IT users and when customer type is not 30 or 60 (360Pay)
  const showMFAField = isITUser() && ![30, 60].includes(customerType);

  let input;
  const [open, setOpen] = useState(false);
  const [usersData, setUsersData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const showSnackbar = (message, backgroundColor = 'red') => {
    setSnackbarMessage(message);
    setSnackbarBackgroundColor(backgroundColor);
    setSnackbarOpen(true);
  };

  const hideSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleOnChange = (event) => {
    if (event?.$isDayjsObject) {
      setUsersData((prevData) => ({
        ...prevData,
        ['arrivalDate']: new Date(event),
      }));
    } else {
      let title = event.target.name;
      let value = event.target.value;
      setUsersData((prevData) => ({
        ...prevData,
        [title]: value,
      }));
    }
  };

  // handle trimming of data when fields lose focus
  const handleOnBlur = (e) => {
    const { name, value } = e.target;
    if (typeof value === 'string') {
      setUsersData((prevData) => ({
        ...prevData,
        [name]: value.trim(),
      }));
    }
  };

  const apiCall = () => {
    setIsSubmitting(true);

    getUserDetails(currentCompanyId)
      .then((data) => {
        store.userDetails[1](data);
        setIsSubmitting(false);
        for (const element of data) {
          element['actions'] = 'Edit';
          if (element['email'] === sessionStorage.getItem('name')) {
            element['name'] = sessionStorage.getItem('contactName');
          }
        }
        setRowData(data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    async function fetchData() {
      try {
        apiCall();
        getUserRoles(currentCompanyId).then((data) => {
          store.userRoles[1](data);
        });
      } catch (err) {
        console.log(err);
      }
    }
    fetchData();
  }, []);

  const handleClose = () => {
    setApiError('');
    setOpen(false);
  };

  const handleClick = (dataClickedItem) => {
    if (Object.keys(dataClickedItem).length > 0 && dataClickedItem.constructor === Object) {
      let itemsRow = dataClickedItem.row;
      for (const rowItem in itemsRow) {
        setUsersData((prevData) => ({
          ...prevData,
          [rowItem]: itemsRow[rowItem],
        }));
      }
    }
  };

  const validateUserForm = (data) => {
    const errors = {};

    const fieldLabels = {
      userEmail: 'Email Address',
      roleId: 'Role',
      name: 'Name',
    };

    const fieldValidation = {
      userEmail: ['required', 'validEmail'],
      roleId: ['required'],
      name: ['required'],
    };

    Object.keys(data).forEach((field) => {
      const validations = fieldValidation[field] || [];

      validations.forEach((validation) => {
        if (validation === 'required' && (data[field] === '' || !data[field])) {
          errors[field] = `${fieldLabels[field]} is required.`;
        } else if (validation === 'validEmail' && !isValidEmail(data[field])) {
          errors[field] = `${fieldLabels[field]} must be a valid email address.`;
        }
      });
    });

    return errors;
  };

  const handleEdit = async () => {
    setIsSubmitting(true);
    setFormErrors({});
    setApiError('');

    const payload = {
      userEmail: usersData.email,
      roleId: input.value,
      name: usersData.name,
      mfaEnable: usersData.mfaEnable,
    };

    try {
      const errors = validateUserForm(payload);

      if (Object.keys(errors).length) {
        setFormErrors(errors);
        setIsSubmitting(false);
      } else {
        const nameParts = payload.name?.trim().split(' ') || [];
        payload.firstname = nameParts[0] || '';
        payload.lastname = nameParts.slice(1).join(' ') || '';

        const response = addUser
          ? await postUserDetails(payload, currentCompanyId)
          : await putUserDetails(payload, currentCompanyId);

        // Check the API's response message
        if (response?.response && response.response === 'User is already created.') {
          setApiError('User already exists');
          setIsSubmitting(false);
          return;
        }
        // Otherwise, assume success and proceed
        setOpen(false);
        setTimeout(apiCall, 1000);
      }
    } catch (e) {
      setApiError(e.response?.data?.message || 'An error occurred while adding the user.');
      setIsSubmitting(false);
      return;
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
    },
    {
      field: 'email',
      headerName: 'Email Address',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
    },
    {
      field: 'lastloggedin',
      headerName: 'Last Logged In',
      type: 'number',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => (
        <div>{params.row.lastloggedin ? dateFormatChange(params.row.lastloggedin, true) : 'Never'}</div>
      ),
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'lastmodified',
      headerName: 'Last Modified',
      type: 'number',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => (
        <div>{params.row.lastmodified ? dateFormatChange(params.row.lastmodified, true) : ''}</div>
      ),
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'disabled',
      headerName: 'Status',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => {
        const isDeleted = params.row.deleted === true;
        if (isDeleted) return 'Deleted';

        const isDisabled = params.row.disabled === true;
        if (isDisabled) return 'Disabled';

        const isActive = params.row.isActive === 1;
        return isActive ? 'Active' : 'Invited';
      },
    },
  ];

  // Conditionally pushing the 'actions' column
  if (sessionStorage.getItem('role') === 'Super') {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) =>
        !disableEditing && (
          <span onClick={handleModalClick} style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}>
            {params.value}
          </span>
        ),
    });
  }

  const handleModalClick = (item) => {
    setAddUser(false);
    setApiError('');
    setFormErrors({});
    setOpen(true);
  };
  const handleModalClickEmpty = () => {
    setUsersData(() => ({}));
    setFormErrors({});
    setApiError('');
    setOpen(true);
    setAddUser(true);
  };

  const disableUserSettings = async (action, userId) => {
    try {
      await disableUser(usersData.email, action, userId);
      setTimeout(apiCall, 1000);
      setOpen(false);
    } catch (err) {
      store.showError('Failed to update user status. Please try again.');
    }
  };

  const handleResendInvite = async () => {
    try {
      setResendLoading(true);
      // Extract first name from the full name
      const nameParts = usersData.name?.trim().split(' ') || [];
      const firstname = nameParts[0] || '';

      const response = await resendUserInvite(currentCompanyId, usersData.email, firstname);

      // Check if the response indicates success
      if (response && response.success) {
        setApiError('');
        showSnackbar('Invite email resent successfully', '#4caf50');
        setOpen(false);
        setTimeout(() => {
          apiCall();
        }, 2000);
      } else {
        // Handle specific error cases from backend
        const errorMessage = response?.message || 'Failed to resend invite. Please try again.';
        showSnackbar(errorMessage, 'red');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to resend invite. Please try again.';
      showSnackbar(errorMessage, 'red');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      <Paper sx={{ width: '100%' }}>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {rowData?.length > 0 && (
              <DataGrid
                rows={rowData}
                columns={columns}
                getRowId={(row) => row.userId}
                initialState={{ pagination: { paginationModel } }}
                pageSizeOptions={[20, 50, 100]}
                sx={{ border: 0 }}
                onRowClick={handleClick}
              />
            )}
            <Dialog
              open={open}
              onClose={handleClose}
              className="settings-dialog"
              PaperProps={{
                component: 'form',
                onSubmit: (event) => {
                  event.preventDefault();
                  const formData = new FormData(event.currentTarget);
                  const formJson = Object.fromEntries(formData.entries());
                  const email = formJson.email;
                  handleClose();
                },
              }}
            >
              <DialogTitle>{usersData.userId ? 'Edit user' : 'Add a new user'}</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {apiError && (
                    <Typography variant="body2" color="error" style={{ marginBottom: '10px' }}>
                      {apiError}
                    </Typography>
                  )}
                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      Email Address*
                    </Typography>
                    {/* Input Field */}
                    {!addUser ? (
                      <div className="readonly-component">{usersData.email}</div>
                    ) : (
                      <TextField
                        className="field-component"
                        value={usersData.email || ''}
                        onChange={handleOnChange}
                        onBlur={handleOnBlur}
                        name="email"
                        error={!!formErrors.userEmail}
                        helperText={formErrors.userEmail}
                      />
                    )}
                  </Box>
                  <Box className="field">
                    {/* Label */}
                    <Typography variant="body1" className="field-label">
                      Name*
                    </Typography>
                    {/* Input Field */}
                    <TextField
                      className="field-component"
                      value={usersData.name || ''}
                      onChange={handleOnChange}
                      onBlur={handleOnBlur}
                      name="name"
                      slotProps={{
                        input: {
                          style: {
                            marginTop: 4,
                          },
                        },
                      }}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                    />
                  </Box>
                  <Box className="field">
                    <InputLabel className="field-label">Role*</InputLabel>
                    <TextField
                      select
                      labelId="submitter-label-2"
                      id="submitter-select-2"
                      value={usersData?.roleId}
                      name="roleId"
                      fullWidth
                      className="field-component"
                      onChange={handleOnChange}
                      inputRef={(node) => {
                        input = node;
                      }}
                      error={!!formErrors.roleId}
                      helperText={formErrors.roleId}
                    >
                      {store.userRoles[0]?.map((item) => (
                        <MenuItem value={item.roleId} key={item.roleId}>
                          {item.department !== 'Haulier' && item.department.replace('-', ' ')}
                          &nbsp;
                          {item.department !== '360PAY-STAFF' && item.role}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  {showMFAField && (
                    <Box className="field" sx={{ display: 'flex', alignItems: 'center' }}>
                      <InputLabel className="field-label">Enable MFA</InputLabel>
                      <Box>
                        <Switch
                          name="mfaEnable"
                          checked={usersData.mfaEnable ?? true}
                          onChange={(e) =>
                            setUsersData((prevData) => ({
                              ...prevData,
                              mfaEnable: e.target.checked,
                            }))
                          }
                        />
                      </Box>
                    </Box>
                  )}
                  {!addUser && (
                    <>
                      <Box className="field">
                        <InputLabel className="field-label">Status</InputLabel>
                        <div>
                          {usersData.disabled === false && (
                            <div className="readonly-component">{usersData.isActive === 1 ? 'Active' : 'Invited'}</div>
                          )}
                          {usersData.disabled === true && <div className="readonly-component">Disabled</div>}
                        </div>
                      </Box>

                      <Box className="field">
                        <InputLabel className="field-label">Last Logged In</InputLabel>
                        <div className="readonly-component">
                          {usersData.lastloggedin ? dateFormatChange(usersData.lastloggedin, true) : 'Never'}
                        </div>
                      </Box>
                    </>
                  )}
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ padding: '24px', justifyContent: 'space-between' }}>
                {!addUser && (
                  <Button
                    className="button-class deny-button"
                    onClick={() => disableUserSettings(!usersData.disabled ? 'disable' : 'delete', usersData?.userId)}
                  >
                    {usersData?.deleted ? 'Restore' : !usersData.disabled ? 'Disable' : 'Delete'} this user
                  </Button>
                )}

                {!addUser && usersData.disabled === null && (
                  <span
                    onClick={resendLoading ? undefined : handleResendInvite}
                    style={{
                      color: resendLoading ? '#666' : '#1976d2',
                      textDecoration: 'underline',
                      cursor: resendLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    {resendLoading ? 'Sending...' : 'Resend Invite'}
                  </span>
                )}

                <Button onClick={handleEdit} className="button-class" disabled={isSubmitting}>
                  Save and Close
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Paper>
      {sessionStorage.getItem('role') === 'Super' && !disableEditing && (
        <Button
          style={{ marginTop: '1rem', marginBottom: '1rem' }}
          className={'button-class'}
          onClick={handleModalClickEmpty}
        >
          Add a New User
        </Button>
      )}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snackbarOpen}
        onClose={hideSnackbar}
        message={snackbarMessage}
        autoHideDuration={4000}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbarBackgroundColor,
            color: 'white',
          },
          mt: 9,
        }}
      />
    </>
  );
}

DataTable.propTypes = {
  companyId: PropTypes.string,
};
